import React, { useEffect, useState } from "react";
import { Grid, CircularProgress, Box} from '@mui/material';
import { getSingleOrgSeasonApi, addSponsorApi, updateSponsorApi, updateSponsorLogoApi, updateSponsorAdImageApi } from '../../api/orgSeasonApi';
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from 'react-router-dom';
import { UploadImageButton } from "../../shared/uploadImageButton";
import { ErrorHeader } from "../../shared/errorHeader";
import { CreateSponsorCommand, OrgSeason, Sponsor } from "../../api/models/orgSeason";
import { BasicTextBox } from "../../shared/basicTextBox";
import { useApi } from "../../hooks/useApi";
import { BasicButton } from "../../shared/basicButton";
import { BasicCheckBox } from "../../shared/basicCheckBox";
import { BasicBackButton } from "../../shared/basicBackButton";
import { BasicDropDown } from "../../shared/basicDropDown";
import { DropDownItem, packageLevelList } from "../../models/dropDownItemViewModel";
import { AddImageCommand } from "../../api/models/common";




export const UpdateSponsorPage: React.FC = (): JSX.Element => {

    const [orgSeason, setOrgSeason] = useState<OrgSeason | null>(null);
    const [currentSponsor, setCurrentSponsor] = useState<Sponsor | null>(null);
    const [sponsorId, setSponsorId] = useState<string | undefined>(undefined);

    const [name, setName] = React.useState('');
    const [nameError, setNameError] = React.useState('');

    const [description, setDescription] = React.useState('');
    const [descriptionError, setDescriptionError] = React.useState('');

    const [website, setWebsite] = React.useState('');
    const [websiteError, setWebsiteError] = React.useState('');

    const [contact, setContact] = React.useState('');
    const [contactError, setContactError] = React.useState('');

    const [packageLevel, setPackageLevel] = React.useState('');
    const [packageLevelError, setPackageLevelError] = React.useState('');

    const [onRoster, setOnRoster] = React.useState<boolean>(false);
    const [onCard, setOnCard] = React.useState<boolean>(false);

    const [logo, setLogo] = React.useState('');
    const [newLogo, setNewLogo] = React.useState('');

    const [adImage, setAdImage] = React.useState('');
    const [newAdImage, setNewAdImage] = React.useState('');

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [accessLevel, setAccessLevel] = useState<string | undefined>(undefined);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [isNew, setIsNew] = useState<boolean>(true);


    const getRequest = useApi(getSingleOrgSeasonApi);
    const updateRequest = useApi(updateSponsorApi);
    const addRequest = useApi(addSponsorApi);
    const updateLogoRequest = useApi(updateSponsorLogoApi);
    const updateAdImageRequest = useApi(updateSponsorAdImageApi);

    const params = useParams();
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();

    useEffect(() => {
        initPage();
    }, [getAccessTokenSilently]);


    useEffect(() => {
        if (getRequest.data) {
            setOrgSeason(getRequest.data);
            prePopulateEdit(getRequest.data.sponsors);
        }
    }, [getRequest.data]);


    useEffect(() => {
        clearPage();
    }, [updateRequest.data]);


    async function initPage() {
        if (!params.orgSeasonId) {
            setErrorMessage('OrgSeason is missing');
        }

        await getRequest.request(params.orgSeasonId);
    }

    function prePopulateEdit(sponsors: Sponsor[]) {

        if (params.sponsorId) {
            if (sponsors && sponsors.length > 0) {
                setSponsorId(params.sponsorId);
                setIsNew(false);

                const findSponsor = sponsors.find(x => x.id === params.sponsorId);
                if (findSponsor) {
                    setCurrentSponsor(findSponsor);
                    setName(findSponsor.name);
                    setDescription(findSponsor.description);
                    setWebsite(findSponsor.website);
                    setContact(findSponsor.contact);
                    setLogo(findSponsor.logoLink);
                    setOnCard(findSponsor.onCard);
                    setOnRoster(findSponsor.onRoster);
                    setPackageLevel(findSponsor.packageLevel);
                    setAdImage(findSponsor.adImageLink);
                }
                else {
                    setErrorMessage('Invalid sponsorId - no sponsors with this ID');
                }
            }
            else {
                setErrorMessage('Invalid sponsorId - no sponsors');
            }
        }


    }

    //event handlers
    const onChangeName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setName(e.target.value);
    }

    const onChangeDescription = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setDescription(e.target.value);
    }

    const onChangeWebsite = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setWebsite(e.target.value);
    }

    const onChangeContact = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setContact(e.target.value);
    }

    const onChangeRoster = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setOnRoster(checked);
    }

    const onChangeCard = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setOnCard(checked);
    }

    const onChangePackageLevel = (e: React.ChangeEvent<HTMLInputElement>) => {
        const packageLevel = e.target.value;
        setPackageLevel(packageLevel);
    }

    const onChangeLogo = async (selectedImage: string): Promise<void> => {
        setLogo(selectedImage);
        setNewLogo(selectedImage);
    }

    const onChangeAdImage = async (selectedImage: string): Promise<void> => {
        setAdImage(selectedImage);
        setNewAdImage(selectedImage);
    }

    async function updateLogoImage() {

        const imageCommand:AddImageCommand = {
            image: newLogo
        };

        await updateLogoRequest.request(params.orgSeasonId!, sponsorId!, imageCommand);

    }

    async function updateAdImage() {

        const imageCommand:AddImageCommand = {
            image: newAdImage
        };

        await updateAdImageRequest.request(params.orgSeasonId!, sponsorId!, imageCommand);

    }

    async function addSponsor() {

        const addSponsorCommand : CreateSponsorCommand = 
        { 
            name: name, 
            description, 
            contact, 
            website, 
            onRoster, 
            onCard, 
            packageLevel,
        };

        if (isNew) {

            addSponsorCommand.logo = newLogo;
            addSponsorCommand.adImage = newAdImage;
            await addRequest.request(params.orgSeasonId!, addSponsorCommand);
        }
        else {
            await updateRequest.request(params.orgSeasonId!, sponsorId!, addSponsorCommand);
        }
    }

    const clearPage = () => {
        setName('');
        setDescription('');
        setWebsite('');
        setContact('');
        setLogo('');
        setAdImage('');
        setPackageLevel('AllStar');
    }

    const mainContent = () => {

        return (
            <Box>
                <h1>{sponsorId ? 'Update Sponsor' : 'Add Sponsor'}</h1>

                <Grid item container spacing={2} direction='column'>
                    <Grid item>

                        <BasicTextBox id='txtName'
                            label='Name*'
                            errorMessage={nameError}
                            required
                            maxLength={20}
                            width={400}
                            value={name}
                            onChange={onChangeName} />

                    </Grid>

                    <Grid item>

                        <BasicTextBox id='txtDescription'
                            label='Description'
                            errorMessage={descriptionError}
                            maxLength={40}
                            width={400}
                            value={description}
                            onChange={onChangeDescription} />
                    </Grid>

                    <Grid item>

                        <BasicTextBox id='txtWebsite'
                            label='Website'
                            errorMessage={websiteError}
                            maxLength={40}
                            width={400}
                            value={website}
                            onChange={onChangeWebsite} />

                    </Grid>

                    <Grid item>

                        <BasicTextBox id='txtContact'
                            label='Contact'
                            errorMessage={contactError}
                            maxLength={40}
                            width={400}
                            value={contact}
                            onChange={onChangeContact} />

                    </Grid>

                    <Grid item>
                        <BasicCheckBox checked={onRoster} onChange={onChangeRoster} label='On Roster' />
                        <BasicCheckBox checked={onCard} onChange={onChangeCard} label='On Card' />

                    </Grid>


                <Grid item>
                <Box>

                <BasicDropDown id='pakageLevel'
                    label='Package Level'
                    width={400}
                    value={packageLevel}
                    items={packageLevelList}
                    onChange={onChangePackageLevel} />
                </Box>
            </Grid>


                    <Grid item>
                        <Box sx={{border: '1px solid #341717ff', padding: '8px', width: '500px'}}>
                            {logo && <img src={logo} height={120} width={180} alt='logo' style={{ border: '1px solid #909090' }} />}
                            <UploadImageButton id="uploadProfile" onChange={onChangeLogo} aspectRatio={1.5} />
                            <p>180 X 160 is the ideal size for the logo</p>
                            {!isNew && <BasicButton onClick={updateLogoImage} labelText="Update" />}
                        </Box>
                        <br/>
                        <br/>
                        <Box sx={{border: '1px solid #341717ff', padding: '8px', width: '500px'}}>
                            {adImage && <img src={adImage} height={120} width={180} alt='adImage' style={{ border: '1px solid #909090' }} />}
                            <UploadImageButton id="uploadAdImage" onChange={onChangeAdImage} aspectRatio={0.625} />
                            <p>600 X 960 is the ideal size for the logo</p>
                            {!isNew && <BasicButton onClick={updateAdImage} labelText="Update" />}
                        </Box>
                    </Grid>


                    <Grid item>
                        {updateRequest.loading && <CircularProgress />}
                        {addRequest.loading && <CircularProgress />}

                        {!updateRequest.loading && !addRequest.loading &&
                            <BasicButton onClick={addSponsor} labelText="Update" />}

                    </Grid>


                </Grid>

            </Box>
        );
    }

    return (<Box>
        <ErrorHeader errorMessage={errorMessage} />
        <Box>{orgSeason && <BasicBackButton labelText='' navigateUrl={`/orgseason/${orgSeason.id}/admin`} />}</Box>
        {isLoading && <CircularProgress />}
        {orgSeason && mainContent()}
    </Box>
    );
}